import type { UserRole } from "@the-drop/types";
import { getTransporterApiBaseUrl } from "./api-base";

export type TransporterFleetDriverDto = {
  driverId: string;
  userId: string;
  fullName: string;
  medEmployeeLicenseNumber: string;
  medLicenseExpiresAt: string;
  coDriversLicenseNumber: string;
  coLicenseExpiresAt: string;
  isActive: boolean;
  onShift: boolean;
  availableForDispatch: boolean;
};

export type TransporterFleetVehicleDto = {
  vehicleId: string;
  make: string;
  model: string;
  year: number;
  color: string;
  licensePlate: string;
  vin: string | null;
  registrationNumber: string | null;
  registrationExpiresAt: string;
  insurancePolicyNumber: string;
  insuranceExpiresAt: string;
  isActive: boolean;
  hasSecurityAlarm: boolean;
  hasLockedOpaqueStorage: boolean;
  hasVideoSurveillance: boolean;
  videoRetentionDays: number;
  gpsDeviceId: string | null;
  maxLoadValueCents: number;
};

export type PatchTransporterFleetDriverBody = {
  medEmployeeLicenseNumber?: string;
  medLicenseExpiresAt?: string;
  coDriversLicenseNumber?: string;
  coLicenseExpiresAt?: string;
  isActive?: boolean;
};

/** Body for **`POST /api/v1/transporters/:id/drivers`** — creates Clerk user + driver row. */
export type CreateTransporterFleetDriverBody = {
  email: string;
  fullName: string;
  phone?: string | null;
  medEmployeeLicenseNumber: string;
  /** `YYYY-MM-DD` */
  medLicenseExpiresAt: string;
  coDriversLicenseNumber: string;
  /** `YYYY-MM-DD` */
  coLicenseExpiresAt: string;
};

export type PatchTransporterFleetVehicleBody = {
  make?: string;
  model?: string;
  year?: number;
  color?: string;
  licensePlate?: string;
  vin?: string | null;
  registrationNumber?: string | null;
  registrationExpiresAt?: string;
  insurancePolicyNumber?: string;
  insuranceExpiresAt?: string;
  hasSecurityAlarm?: boolean;
  hasLockedOpaqueStorage?: boolean;
  hasVideoSurveillance?: boolean;
  videoRetentionDays?: number;
  gpsDeviceId?: string | null;
  maxLoadValueCents?: number;
  isActive?: boolean;
};

export type TransporterFleetStatusResponse = {
  drivers: TransporterFleetDriverDto[];
  vehicles: TransporterFleetVehicleDto[];
};

export type TransporterActiveRunDto = {
  id: string;
  status: string;
  dispensaryId: string;
  dispensaryLegalName: string;
  driverId: string;
  driverFullName: string;
  updatedAt: string;
  metrcManifestNumber: string | null;
  manifestStatus: string;
  /**
   * Distinct `sales_orders` on this run that still have at least one
   * non-terminal stop. The dashboard driver card uses this to decide
   * "In Transit" vs "Available" — an open shift with zero in-flight
   * orders reads "Available", not "In Transit". Only present on the
   * `…/runs/active` payload; history rows are terminal by definition
   * and the field is undefined (callers should treat as 0).
   */
  inFlightOrderCount?: number;
};

export type TransporterActiveRunsResponse = {
  runs: TransporterActiveRunDto[];
};

export type TransporterRunHistoryDto = TransporterActiveRunDto & {
  completedAt: string | null;
};

export type TransporterRunHistoryResponse = {
  runs: TransporterRunHistoryDto[];
};

export type TransporterRunDetailStopDto = {
  id: string;
  salesOrderId: string;
  sequenceNumber: number;
  status: string;
  deliveredAt: string | null;
};

export type TransporterRunDetailResponse = {
  run: {
    id: string;
    status: string;
    dispensaryLegalName: string;
    metrcManifestNumber: string | null;
    manifestStatus: string;
  };
  stops: TransporterRunDetailStopDto[];
};

export type TransporterEligibleDispatchRunDto = TransporterActiveRunDto;

export type TransporterEligibleDispatchRunsResponse = {
  runs: TransporterEligibleDispatchRunDto[];
};

export type TransporterEarningsTotalsDto = {
  grossTransporterFeesCents: number;
  transporterPlatformFeesCents: number;
  netTransporterEarningsCents: number;
  completedDeliveryCount: number;
};

export type TransporterEarningsDailyDto = {
  date: string;
  grossTransporterFeesCents: number;
  transporterPlatformFeesCents: number;
  netTransporterEarningsCents: number;
  completedDeliveryCount: number;
};

export type TransporterEarningsSettlementDto = {
  batchStatementId: string;
  dispensaryId: string;
  dispensaryLegalName: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  transporterFeeTotalCents: number;
  transporterPlatformFeeTotalCents: number;
  netTransporterEarningsCents: number;
  successfulDeliveryCount: number;
  settlementCompletedAt: string | null;
  straddleTransferIdInbound: string | null;
  straddleStatusInbound: string | null;
  straddleTransferIdPlatformFee: string | null;
  straddleStatusPlatformFee: string | null;
};

export type TransporterEarningsSummaryResponse = {
  currency: "USD";
  asOf: string;
  monthToDateStartsAt: string;
  monthToDate: TransporterEarningsTotalsDto;
  allTime: TransporterEarningsTotalsDto;
  /** Server SQL window for **`daily`** buckets (**7 | 30 | 90 | 180**). */
  dailyLookbackDays: number;
  daily: TransporterEarningsDailyDto[];
  settlements: TransporterEarningsSettlementDto[];
};

/** Mirrors **`PublicTransporter`** from **`GET /api/v1/transporters/:transporterId`**. */
export type TransporterProfileDto = {
  id: string;
  clerkOrgId: string | null;
  legalName: string;
  dbaName: string | null;
  medLicenseNumber: string;
  medLicenseExpiresAt: string;
  hasDeliveryPermit: boolean;
  deliveryPermitNumber: string | null;
  deliveryPermitExpiresAt: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  secrets: {
    straddlePaykeyConfigured: boolean;
    plaidItemConfigured: boolean;
    plaidAccessTokenConfigured: boolean;
  };
  straddleAccountId: string | null;
  bankAccountLast4: string | null;
  bankName: string | null;
  isActive: boolean;
  onboardingCompletedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

async function authFetch(
  getToken: () => Promise<string | null>,
  path: string,
): Promise<Response> {
  const token = await getToken();
  if (!token) {
    throw new Error("not_signed_in");
  }
  const base = getTransporterApiBaseUrl();
  const url = base ? `${base}${path}` : path;
  return fetch(url, { headers: { Authorization: `Bearer ${token}` } });
}

async function authPatchJson(
  getToken: () => Promise<string | null>,
  path: string,
  body: Record<string, unknown>,
): Promise<Response> {
  const token = await getToken();
  if (!token) {
    throw new Error("not_signed_in");
  }
  const base = getTransporterApiBaseUrl();
  const url = base ? `${base}${path}` : path;
  return fetch(url, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

async function authPostJson(
  getToken: () => Promise<string | null>,
  path: string,
  body: Record<string, unknown>,
): Promise<Response> {
  const token = await getToken();
  if (!token) {
    throw new Error("not_signed_in");
  }
  const base = getTransporterApiBaseUrl();
  const url = base ? `${base}${path}` : path;
  return fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

export async function patchTransporterFleetDriver(
  getToken: () => Promise<string | null>,
  transporterId: string,
  driverId: string,
  body: PatchTransporterFleetDriverBody,
): Promise<{ driver: TransporterFleetDriverDto }> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/drivers/${encodeURIComponent(driverId)}`;
  const res = await authPatchJson(getToken, path, body as Record<string, unknown>);
  if (!res.ok) {
    throw new Error(`fleet_driver_patch_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as { driver: TransporterFleetDriverDto };
}

export type CreateTransporterFleetDriverResponse = {
  driver: TransporterFleetDriverDto;
  /** True when Clerk accepted `createInvitation` with `notify` (check spam / Clerk deliverability). */
  clerkInvitationEmailSent: boolean;
  clerkInvitationError?: string;
};

export async function createTransporterFleetDriver(
  getToken: () => Promise<string | null>,
  transporterId: string,
  body: CreateTransporterFleetDriverBody,
): Promise<CreateTransporterFleetDriverResponse> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/drivers`;
  const res = await authPostJson(getToken, path, body as Record<string, unknown>);
  if (!res.ok) {
    throw new Error(`fleet_driver_create_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as CreateTransporterFleetDriverResponse;
}

export async function patchTransporterFleetVehicle(
  getToken: () => Promise<string | null>,
  transporterId: string,
  vehicleId: string,
  body: PatchTransporterFleetVehicleBody,
): Promise<{ vehicle: TransporterFleetVehicleDto }> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/vehicles/${encodeURIComponent(vehicleId)}`;
  const res = await authPatchJson(getToken, path, body as Record<string, unknown>);
  if (!res.ok) {
    throw new Error(`fleet_vehicle_patch_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as { vehicle: TransporterFleetVehicleDto };
}

async function httpErrorDetail(res: Response): Promise<string> {
  try {
    const j = (await res.clone().json()) as { error?: string; message?: string; detail?: string };
    const main = j.error ?? j.message;
    const extra = j.detail != null && String(j.detail).length > 0 ? String(j.detail) : "";
    const combined =
      main != null && String(main).length > 0
        ? extra.length > 0
          ? `${String(main)} — ${extra}`
          : String(main)
        : extra;
    return combined.length > 0 ? `: ${combined}` : "";
  } catch {
    return "";
  }
}

export async function fetchTransporterFleetStatus(
  getToken: () => Promise<string | null>,
  transporterId: string,
): Promise<TransporterFleetStatusResponse> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/fleet/status`;
  const res = await authFetch(getToken, path);
  if (!res.ok) {
    throw new Error(`fleet_status_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as TransporterFleetStatusResponse;
}

export async function fetchTransporterActiveRuns(
  getToken: () => Promise<string | null>,
  transporterId: string,
): Promise<TransporterActiveRunsResponse> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/runs/active`;
  const res = await authFetch(getToken, path);
  if (!res.ok) {
    throw new Error(`runs_active_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as TransporterActiveRunsResponse;
}

export async function fetchTransporterRunHistory(
  getToken: () => Promise<string | null>,
  transporterId: string,
  opts?: { limit?: number; status?: "completed" },
): Promise<TransporterRunHistoryResponse> {
  const q = new URLSearchParams();
  if (opts?.limit != null) q.set("limit", String(opts.limit));
  if (opts?.status != null) q.set("status", opts.status);
  const qs = q.toString();
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/runs/history${qs ? `?${qs}` : ""}`;
  const res = await authFetch(getToken, path);
  if (!res.ok) {
    throw new Error(`runs_history_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as TransporterRunHistoryResponse;
}

export async function fetchTransporterRunDetail(
  getToken: () => Promise<string | null>,
  transporterId: string,
  runId: string,
): Promise<TransporterRunDetailResponse> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/runs/${encodeURIComponent(runId)}`;
  const res = await authFetch(getToken, path);
  if (!res.ok) {
    throw new Error(`run_detail_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as TransporterRunDetailResponse;
}

export async function fetchTransporterEligibleDispatchRuns(
  getToken: () => Promise<string | null>,
  transporterId: string,
): Promise<TransporterEligibleDispatchRunsResponse> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/dispatch/eligible-runs`;
  const res = await authFetch(getToken, path);
  if (!res.ok) {
    throw new Error(`eligible_runs_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as TransporterEligibleDispatchRunsResponse;
}

export async function fetchTransporterProfile(
  getToken: () => Promise<string | null>,
  transporterId: string,
): Promise<TransporterProfileDto> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}`;
  const res = await authFetch(getToken, path);
  if (!res.ok) {
    throw new Error(`transporter_profile_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as TransporterProfileDto;
}

export type TransporterTeamUserDto = {
  id: string;
  role: UserRole;
  fullName: string;
  email: string;
  phone: string | null;
  isActive: boolean;
  createdAt: string;
};

export async function fetchTransporterTeamUsers(
  getToken: () => Promise<string | null>,
  transporterId: string,
): Promise<TransporterTeamUserDto[]> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/users`;
  const res = await authFetch(getToken, path);
  if (!res.ok) {
    throw new Error(`transporter_team_http_${res.status}${await httpErrorDetail(res)}`);
  }
  const j = (await res.json()) as { users: TransporterTeamUserDto[] };
  return j.users;
}

/** Subset of `PatchTransporterBody` for org + contact self-service (no MED/bank secrets). */
export type TransporterOrgContactPatchBody = {
  legalName: string;
  dbaName: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
};

async function patchTransporterJson(
  getToken: () => Promise<string | null>,
  transporterId: string,
  body: Record<string, unknown>,
): Promise<TransporterProfileDto> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}`;
  const token = await getToken();
  if (!token) {
    throw new Error("not_signed_in");
  }
  const base = getTransporterApiBaseUrl();
  const url = base ? `${base}${path}` : path;
  const res = await fetch(url, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`transporter_profile_patch_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as TransporterProfileDto;
}

export async function patchTransporterProfile(
  getToken: () => Promise<string | null>,
  transporterId: string,
  body: TransporterOrgContactPatchBody,
): Promise<TransporterProfileDto> {
  return patchTransporterJson(getToken, transporterId, body as Record<string, unknown>);
}

/**
 * KYB / onboarding: org contact + Colorado MED license + delivery permit (**`PATCH /api/v1/transporters/:id`**).
 */
export type TransporterKybPatchBody = TransporterOrgContactPatchBody & {
  medLicenseNumber: string;
  /** `YYYY-MM-DD` */
  medLicenseExpiresAt: string;
  hasDeliveryPermit: boolean;
  deliveryPermitNumber: string | null;
  /** `YYYY-MM-DD` or null */
  deliveryPermitExpiresAt: string | null;
};

export async function patchTransporterKybProfile(
  getToken: () => Promise<string | null>,
  transporterId: string,
  body: TransporterKybPatchBody,
): Promise<TransporterProfileDto> {
  return patchTransporterJson(getToken, transporterId, body as Record<string, unknown>);
}

/** Payout / bank fields for **`PATCH /api/v1/transporters/:id`**. Omit secret strings to leave stored values unchanged. */
export function buildTransporterPayoutPatchBody(f: {
  straddleAccountId: string | null;
  bankName: string | null;
  bankAccountLast4: string | null;
  straddlePaykey: string;
  plaidItemId: string;
  plaidAccessToken: string;
}): Record<string, unknown> {
  const last4 = f.bankAccountLast4?.replace(/\D/g, "").slice(0, 4);
  const body: Record<string, unknown> = {
    straddleAccountId: f.straddleAccountId?.trim() ? f.straddleAccountId.trim() : null,
    bankName: f.bankName?.trim() ? f.bankName.trim() : null,
    bankAccountLast4: last4 && last4.length > 0 ? last4 : null,
  };
  const pk = f.straddlePaykey.trim();
  if (pk.length > 0) body.straddlePaykey = pk;
  const pi = f.plaidItemId.trim();
  if (pi.length > 0) body.plaidItemId = pi;
  const pa = f.plaidAccessToken.trim();
  if (pa.length > 0) body.plaidAccessToken = pa;
  return body;
}

export async function patchTransporterPayoutFields(
  getToken: () => Promise<string | null>,
  transporterId: string,
  body: Record<string, unknown>,
): Promise<TransporterProfileDto> {
  return patchTransporterJson(getToken, transporterId, body);
}

export async function fetchTransporterEarningsSummary(
  getToken: () => Promise<string | null>,
  transporterId: string,
  opts?: { dailyLookbackDays?: 7 | 30 | 90 | 180 },
): Promise<TransporterEarningsSummaryResponse> {
  /** §15.3 singular path + query — also avoids proxies that mishandle deep **`/transporters/:id/...`** segments. */
  const q = new URLSearchParams({ transporterId });
  if (opts?.dailyLookbackDays != null) {
    q.set("dailyLookbackDays", String(opts.dailyLookbackDays));
  }
  const path = `/api/v1/transporter/earnings/summary?${q.toString()}`;
  const res = await authFetch(getToken, path);
  if (!res.ok) {
    throw new Error(`earnings_summary_http_${res.status}${await httpErrorDetail(res)}`);
  }
  return (await res.json()) as TransporterEarningsSummaryResponse;
}

export async function postTransporterManualAssign(
  getToken: () => Promise<string | null>,
  transporterId: string,
  body: { runId: string; driverId: string },
): Promise<void> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/dispatch/manual-assign`;
  const token = await getToken();
  if (!token) {
    throw new Error("not_signed_in");
  }
  const base = getTransporterApiBaseUrl();
  const url = base ? `${base}${path}` : path;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`manual_assign_http_${res.status}${await httpErrorDetail(res)}`);
  }
}
